
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Sparkles } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { executeSearchStrategy } from "./search/SearchOrchestrator";
import { toast } from "sonner";

interface VacationChatProps {
  searchQuery: string;
  vacationCount: number;
  onAiRefineSearch: (refinement: string) => void;
  onResetFilters: () => void;
  isFiltered: boolean;
}

export const VacationChat = ({ 
  searchQuery, 
  vacationCount, 
  onAiRefineSearch, 
  onResetFilters, 
  isFiltered 
}: VacationChatProps) => {
  const [refinement, setRefinement] = useState("");
  const [isRefining, setIsRefining] = useState(false);
  const isMobile = useIsMobile();

  const handleRefine = async () => {
    if (refinement.trim()) {
      setIsRefining(true);
      try {
        // Use the same smart search strategy as homepage
        await executeSearchStrategy(refinement.trim(), (results) => {
          // Pass the results to the parent component
          onAiRefineSearch(refinement.trim());
          toast.success(`${results.length} vakanties gevonden met slimme verfijning!`);
        });
        setRefinement("");
      } catch (error) {
        console.error('Refinement search error:', error);
        // Fallback to original refinement method
        onAiRefineSearch(refinement.trim());
        setRefinement("");
      } finally {
        setIsRefining(false);
      }
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    setRefinement(suggestion);
    setIsRefining(true);
    try {
      // Use the same smart search strategy as homepage
      await executeSearchStrategy(suggestion, (results) => {
        // Pass the results to the parent component
        onAiRefineSearch(suggestion);
        toast.success(`${results.length} vakanties gevonden met slimme suggestie!`);
      });
    } catch (error) {
      console.error('Suggestion search error:', error);
      // Fallback to original refinement method
      onAiRefineSearch(suggestion);
    } finally {
      setIsRefining(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRefine();
    }
  };

  const suggestions = isMobile 
    ? [
        "met zwembad",
        "all inclusive", 
        "max €600",
        "families",
        "wellness"
      ]
    : [
        "met zwembad",
        "all inclusive",
        "maximaal €600",
        "voor families",
        "wellness faciliteiten"
      ];

  return (
    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
      <div className={`flex items-center gap-2 mb-3 ${isMobile ? 'justify-center' : ''}`}>
        <Sparkles className="h-5 w-5 text-orange-600" />
        <h3 className="font-medium text-orange-700">
          {isMobile ? "Kan Zonnie je verder helpen met deze vakantie?" : "Kan Zonnie je verder helpen met deze vakantie?"}
        </h3>
      </div>
      
      <div className="flex gap-2 mb-3">
        <Input
          placeholder={isMobile ? "Bijv. 'met zwembad'..." : "Bijv. 'met zwembad' of 'maximaal €600'..."}
          value={refinement}
          onChange={(e) => setRefinement(e.target.value)}
          onKeyPress={handleKeyPress}
          className="border-orange-200 focus:border-orange-400"
          disabled={isRefining}
        />
        <Button
          onClick={handleRefine}
          disabled={!refinement.trim() || isRefining}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {isRefining ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div className={`flex items-center ${isMobile ? 'justify-between' : 'justify-start gap-4'} mb-2`}>
        <div className="text-sm text-gray-600">
          Populair:
        </div>
        {isMobile && isFiltered && (
          <Button
            variant="outline"
            size="sm"
            onClick={onResetFilters}
            className="text-orange-600 border-orange-200 hover:bg-orange-100 text-xs"
          >
            Filters weghalen
          </Button>
        )}
      </div>
      
      <div className={`flex flex-wrap gap-2 ${isMobile ? 'justify-end' : ''}`}>
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion}
            variant="outline"
            size="sm"
            onClick={() => handleSuggestionClick(suggestion)}
            className="text-orange-600 border-orange-200 hover:bg-orange-100 text-xs"
            disabled={isRefining}
          >
            {suggestion}
          </Button>
        ))}
      </div>
    </div>
  );
};
